import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styles from './PlaceOrder.module.css';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Moment from 'moment';

const SLOT_MINUTES = [0, 15, 30, 45];
const DEFAULT_START_HOUR = 6;
const DEFAULT_END_HOUR = 23;
const PANEL_WIDTH = 312;
const PANEL_MAX_HEIGHT = 470;
const VIEWPORT_GUTTER = 12;

const isSameDay = (a, b) =>
  a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();

const minutesOfDay = (date) => date.getHours() * 60 + date.getMinutes();

const startOfDay = (date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

const addDays = (date, days) => {
  const next = startOfDay(date);
  next.setDate(next.getDate() + days);
  return next;
};

export const DateTimeField = (props) => {
  const { value, onChange, minDate, minTime, maxTime, placeholder, className, inputId } = props;

  const containerRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const [selectedDate, setSelectedDate] = useState(value ? new Date(value) : null);
  const [meridiem, setMeridiem] = useState(value ? (new Date(value).getHours() < 12 ? "AM" : "PM") : "AM");

  useEffect(() => {
    if (value) {
      const next = new Date(value);
      setSelectedDate(next);
      setMeridiem(next.getHours() < 12 ? "AM" : "PM");
    } else {
      setSelectedDate(null);
    }
  }, [value]);

  const updatePosition = useCallback(() => {
    const anchor = containerRef.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const viewportHeight = window.innerHeight || 0;
    const viewportWidth = window.innerWidth || 0;
    const panelHeight = Math.min(panelRef.current ? panelRef.current.offsetHeight : PANEL_MAX_HEIGHT, PANEL_MAX_HEIGHT);

    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const dropUp = spaceBelow < panelHeight + VIEWPORT_GUTTER && spaceAbove > spaceBelow;

    const available = (dropUp ? spaceAbove : spaceBelow) - VIEWPORT_GUTTER * 2;
    const height = Math.max(220, Math.min(PANEL_MAX_HEIGHT, available));

    setPosition({
      top: dropUp ? Math.max(VIEWPORT_GUTTER, rect.top - height - 8) : rect.bottom + 8,
      left: Math.max(VIEWPORT_GUTTER, Math.min(rect.left, viewportWidth - PANEL_WIDTH - VIEWPORT_GUTTER)),
      maxHeight: height,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, selectedDate, meridiem, updatePosition]);

  useEffect(() => {
    if (!open) return;

    const onClickOutside = (e) => {
      const insideAnchor = containerRef.current && containerRef.current.contains(e.target);
      const insidePanel = panelRef.current && panelRef.current.contains(e.target);
      if (!insideAnchor && !insidePanel) setOpen(false);
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopImmediatePropagation();
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, updatePosition]);

  const slots = useMemo(() => {
    const day = selectedDate ? new Date(selectedDate) : null;
    if (!day) return [];

    const now = new Date();
    const lowerBound = minTime ? minutesOfDay(new Date(minTime)) : DEFAULT_START_HOUR * 60;
    const upperBound = maxTime ? minutesOfDay(new Date(maxTime)) : DEFAULT_END_HOUR * 60;

    const generated = [];
    for (let hour = 0; hour <= DEFAULT_END_HOUR; hour++) {
      for (const minute of SLOT_MINUTES) {
        const slotMinutes = hour * 60 + minute;
        if (slotMinutes < lowerBound || slotMinutes > upperBound) continue;

        const slot = new Date(day);
        slot.setHours(hour, minute, 0, 0);

        if (isSameDay(slot, now) && slot.getTime() <= now.getTime()) continue;

        generated.push(slot);
      }
    }

    return generated;
  }, [selectedDate, minTime, maxTime]);

  const visibleSlots = slots.filter(slot => (meridiem === "AM" ? slot.getHours() < 12 : slot.getHours() >= 12));

  const onDateSelected = (date) => {
    const next = new Date(date);
    setSelectedDate(next);

    // keep the previously chosen time when it is still bookable, otherwise fall back
    // to the first available slot of the newly selected day
    const now = new Date();
    const lowerBound = minTime ? minutesOfDay(new Date(minTime)) : DEFAULT_START_HOUR * 60;
    const upperBound = maxTime ? minutesOfDay(new Date(maxTime)) : DEFAULT_END_HOUR * 60;
    const currentMinutes = value ? minutesOfDay(new Date(value)) : null;

    if (currentMinutes !== null && currentMinutes >= lowerBound && currentMinutes <= upperBound) {
      next.setHours(Math.floor(currentMinutes / 60), currentMinutes % 60, 0, 0);
      if (!isSameDay(next, now) || next.getTime() > now.getTime()) {
        setMeridiem(next.getHours() < 12 ? "AM" : "PM");
        onChange(next);
        return;
      }
    }

    for (let hour = 0; hour <= DEFAULT_END_HOUR; hour++) {
      for (const minute of SLOT_MINUTES) {
        const slotMinutes = hour * 60 + minute;
        if (slotMinutes < lowerBound || slotMinutes > upperBound) continue;

        const slot = new Date(next);
        slot.setHours(hour, minute, 0, 0);
        if (isSameDay(slot, now) && slot.getTime() <= now.getTime()) continue;

        setMeridiem(slot.getHours() < 12 ? "AM" : "PM");
        onChange(slot);
        return;
      }
    }
  }

  const onSlotSelected = (slot) => {
    onChange(new Date(slot));
    setOpen(false);
  }

  const selectedTimeLabel = value ? Moment(value).format("h:mm A") : "";
  const earliestDay = minDate ? startOfDay(minDate) : startOfDay(new Date());
  const shortcuts = [
    { label: "Today", date: startOfDay(new Date()) },
    { label: "Tomorrow", date: addDays(new Date(), 1) },
  ].filter(shortcut => shortcut.date.getTime() >= earliestDay.getTime());

  const renderHeader = ({ monthDate, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
    <div className={styles.dtCalHead}>
      <button
        type="button"
        className={styles.dtCalNav}
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        aria-label="Previous month">
        <i className="fas fa-chevron-left" />
      </button>
      <span className={styles.dtCalMonth}>{Moment(monthDate).format("MMMM YYYY")}</span>
      <button
        type="button"
        className={styles.dtCalNav}
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        aria-label="Next month">
        <i className="fas fa-chevron-right" />
      </button>
    </div>
  );

  const panel = (
    <div
      ref={panelRef}
      className={styles.dateTimePanel}
      style={position ? { top: position.top, left: position.left, maxHeight: position.maxHeight } : { visibility: "hidden" }}>

      {
        shortcuts.length > 0 &&
        <div className={styles.dtShortcuts}>
          {
            shortcuts.map(shortcut => {
              const isActive = selectedDate && isSameDay(selectedDate, shortcut.date);
              return (
                <button
                  type="button"
                  key={shortcut.label}
                  className={`${styles.dtShortcut} ${isActive ? styles.dtShortcutOn : ""}`}
                  onClick={() => onDateSelected(shortcut.date)}>
                  {shortcut.label}
                </button>
              );
            })
          }
        </div>
      }

      <div className={styles.dtCalendar}>
        <DatePicker
          selected={selectedDate}
          onChange={onDateSelected}
          minDate={minDate}
          renderCustomHeader={renderHeader}
          formatWeekDay={day => day.slice(0, 3)}
          inline
        />
      </div>

      <div className={styles.dtTimeHead}>
        <span className={styles.dtTimeLabel}>Time</span>
        <div className={styles.meridiemToggle}>
          <button
            type="button"
            className={meridiem === "AM" ? styles.meridiemOptionActive : styles.meridiemOption}
            onClick={() => setMeridiem("AM")}>AM</button>
          <button
            type="button"
            className={meridiem === "PM" ? styles.meridiemOptionActive : styles.meridiemOption}
            onClick={() => setMeridiem("PM")}>PM</button>
        </div>
      </div>

      <div className={styles.timeSlotContainer}>
        {
          !selectedDate &&
          <p className={styles.dtEmpty}>Select a date to see the available times.</p>
        }
        {
          selectedDate && visibleSlots.length === 0 &&
          <p className={styles.dtEmpty}>No {meridiem} slots available for this date.</p>
        }
        {
          visibleSlots.map(slot => {
            const label = Moment(slot).format("h:mm A");
            const isSelected = label === selectedTimeLabel && selectedDate && isSameDay(slot, selectedDate);
            return (
              <button
                type="button"
                key={slot.getTime()}
                className={isSelected ? styles.timeSlotActive : styles.timeSlot}
                onClick={() => onSlotSelected(slot)}>
                {label}
              </button>
            );
          })
        }
      </div>
    </div>
  );

  return (
    <div className={styles.dateTimeFieldContainer} ref={containerRef}>
      <input
        id={inputId}
        className={className}
        value={value ? Moment(value).format("D MMM YYYY, h:mm A") : ""}
        placeholder={placeholder}
        onChange={() => { }}
        onClick={() => setOpen(!open)}
        readOnly
      />

      {open && ReactDOM.createPortal(panel, document.body)}
    </div>
  )
}
